import log from "./logger";
import { appStore, AuthTokenLocalStorageKey } from "./models/AppStore";

// Ссылка на безопасное хранилище Яндекса. Изначально null.
let yandexSafeStorage: Storage | null = null;

let currentAuthToken: string | null = null;

/**
 * Регистрирует SafeStorage от Яндекс.Игр.
 * Вызывается из адаптера после инициализации YSDK.
 * @param storage - SafeStorage объект из Yandex SDK
 */
export function registerYandexStorage(storage: Storage): void {
	yandexSafeStorage = storage;
	log.info("Yandex SafeStorage registered in Auth module");

	// Попытка восстановить сессию, если localStorage был очищен (проблема iOS)
	try {
		const yaToken = storage.getItem(AuthTokenLocalStorageKey);
		const localToken = localStorage.getItem(AuthTokenLocalStorageKey);

		if (yaToken && !localToken) {
			log.info("Restoring auth token from Yandex SafeStorage to LocalStorage");
			setAuthToken(yaToken);
		} else if (currentAuthToken && !yaToken) {
			// Если у нас есть токен в памяти/local, но нет в яндексе - синхронизируем в яндекс
			try {
				storage.setItem(AuthTokenLocalStorageKey, currentAuthToken);
			} catch (e) {
				log.error("Failed to sync token to Yandex storage", e);
			}
		}
	} catch (err) {
		log.error("Error during Yandex storage initialization", err);
	}
}

export function resetAuthToken(): void {
	currentAuthToken = null;

	// Очищаем localStorage
	try {
		localStorage.removeItem(AuthTokenLocalStorageKey);
	} catch (err) {
		log.error("Failed to clear localStorage", err);
	}

	// Очищаем Yandex storage
	if (yandexSafeStorage) {
		try {
			yandexSafeStorage.removeItem(AuthTokenLocalStorageKey);
		} catch (err) {
			log.error("Failed to clear Yandex storage", err);
		}
	}

	// Update appStore
	appStore.getState().setAuthToken(null);
}

export function setAuthToken(token: string): void {
	currentAuthToken = token;

	// 1. Пишем в LocalStorage (стандартное поведение)
	try {
		localStorage.setItem(AuthTokenLocalStorageKey, token);
	} catch (err) {
		log.error("Failed to write to localStorage", err);
	}

	// 2. Дублируем в Яндекс (если доступен)
	if (yandexSafeStorage) {
		try {
			yandexSafeStorage.setItem(AuthTokenLocalStorageKey, token);
		} catch (err) {
			log.error("Failed to write to Yandex storage", err);
		}
	}

	// 3. Update appStore
	appStore.setState((state) => {
		state.authToken = token;
	});
}

export function getAuthToken(): string | null {
	let token: string | null = null;

	// 1. Пробуем читать из LocalStorage
	try {
		token = localStorage.getItem(AuthTokenLocalStorageKey);
	} catch (err) {
		log.error("Failed to read from localStorage", err);
	}

	// 2. Если не нашли и есть Яндекс хранилище - пробуем оттуда
	if (!token && yandexSafeStorage) {
		try {
			token = yandexSafeStorage.getItem(AuthTokenLocalStorageKey);
			// Если нашли в Яндексе, но не было в LocalStorage - синхронизируем
			if (token) {
				currentAuthToken = token;
			}
		} catch (err) {
			log.error("Failed to read from Yandex storage", err);
		}
	}

	// 3. Фолбек на переменную в памяти
	return token || currentAuthToken;
}

export function authTokenExists(): boolean {
	try {
		// Проверка в LocalStorage
		if (localStorage.getItem(AuthTokenLocalStorageKey) != null) return true;

		// Проверка в Yandex Storage
		if (yandexSafeStorage && yandexSafeStorage.getItem(AuthTokenLocalStorageKey) != null) return true;
	} catch (err) {
		log.error("Failed to check auth token existence", err);
	}
	return currentAuthToken != null;
}
